import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import api, { SelectOption } from '../services/api';
import { useAuth } from './AuthContext';

interface ProjectContextType {
    projects: SelectOption[];
    currentProject: SelectOption | null;
    setCurrentProject: (project: SelectOption | null) => void;
    isLoading: boolean;
    refreshProjects: () => Promise<void>;
}

const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

export const ProjectProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { isAuthenticated } = useAuth();
    const location = useLocation();
    // V8.1: Sync with URL params
    // We can't use useParams() here because Provider might be outside the specific Route, 
    // but we can look at window.location or useLocation if inside Router (which we enabled).
    // Let's rely on components calling handleSetCurrentProject OR basic parsing.
    // Better: Allow components to "mount" the project.
    // For now, let's keep the existing logic but make sure we don't accidentally override valid URL navigation.

    // Note: In V8.1 we moved ProjectProvider inside Router, so we could theoretically use useLocation.
    // However, for simplicity and stability, we will let the existing logic run, 
    // but ProjectHome/Wrapper components will perform the switch.

    const [projects, setProjects] = useState<SelectOption[]>([]);
    const [currentProject, setCurrentProjectState] = useState<SelectOption | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    const refreshProjects = async () => {
        try {
            setIsLoading(true);
            const data = await api.getProjects();
            // console.log("📂 Projects loaded:", data.length);

            const options: SelectOption[] = data
                .filter((p: any) => p && p.id && p.name)
                .map((p: any) => ({
                    value: p.id,
                    label: p.name
                }));

            setProjects(options);

            // V8.1 Logic: If URL contains /projects/:id, try to select that one.
            const pathParts = window.location.pathname.split('/');
            const projectIndex = pathParts.indexOf('projects');
            let urlProjectId: string | undefined;
            if (projectIndex !== -1 && pathParts.length > projectIndex + 1) {
                const candidate = pathParts[projectIndex + 1];
                if (candidate && candidate !== 'new') {
                    urlProjectId = candidate;
                }
            }

            if (options.length > 0) {
                if (urlProjectId) {
                    const foundUrl = options.find(p => p.value === urlProjectId);
                    if (foundUrl) {
                        setCurrentProjectState(foundUrl);
                        localStorage.setItem('currentProjectId', foundUrl.value);
                        return; // Done
                    }
                }

                // Fallback to localStorage
                const savedId = localStorage.getItem('currentProjectId');
                const found = options.find(p => p.value === savedId);

                if (found) {
                    setCurrentProjectState(found);
                } else {
                    // Default to first if strict mode not set
                    setCurrentProjectState(options[0]);
                    localStorage.setItem('currentProjectId', options[0].value);
                }
            }
        } catch (error) {
            console.error("❌ Failed to load projects", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (isAuthenticated) {
            refreshProjects();
        } else {
            setProjects([]);
            setCurrentProjectState(null);
        }
    }, [isAuthenticated]);

    // V8.1 Hotfix: Sync current project when URL changes
    useEffect(() => {
        if (projects.length === 0) return;

        const pathParts = location.pathname.split('/');
        const projectIndex = pathParts.indexOf('projects');

        if (projectIndex !== -1 && pathParts.length > projectIndex + 1) {
            const urlProjectId = pathParts[projectIndex + 1];
            if (urlProjectId && urlProjectId !== 'new') {
                const foundProject = projects.find(p => p.value === urlProjectId);
                if (foundProject && foundProject.value !== currentProject?.value) {
                    console.log("📂 URL changed, syncing project:", foundProject);
                    setCurrentProjectState(foundProject);
                    localStorage.setItem('currentProjectId', foundProject.value);
                }
            }
        }
    }, [location.pathname, projects]);


    const handleSetCurrentProject = (project: SelectOption | null) => {
        console.log("📂 Setting current project:", project);
        setCurrentProjectState(project);
        if (project) {
            localStorage.setItem('currentProjectId', project.value);
        } else {
            localStorage.removeItem('currentProjectId');
        }
    };

    return (
        <ProjectContext.Provider value={{
            projects,
            currentProject,
            setCurrentProject: handleSetCurrentProject,
            isLoading,
            refreshProjects
        }}>
            {children}
        </ProjectContext.Provider>
    );
};

export const useProject = () => {
    const context = useContext(ProjectContext);
    if (context === undefined) {
        throw new Error('useProject must be used within a ProjectProvider');
    }
    return context;
};

